import type {
  RenderAssetDetail,
  RenderAssetListItem,
  RenderAssetPricingSummary,
} from "@/lib/api-types";

type PriceSummaryLike = Pick<
  RenderAssetPricingSummary,
  "defaultPrice" | "minPrice" | "maxPrice" | "uniform"
>;
type DefaultTextureAsset = Pick<
  RenderAssetDetail,
  "defaultTextureName" | "textureVariants"
>;
type RenderAssetRouteTarget = Pick<
  RenderAssetListItem,
  | "id"
  | "collectionName"
  | "pedGender"
  | "kind"
  | "slotId"
  | "drawableLocalIndex"
  | "meshPartIndex"
>;
type ClothingShopHashTarget = Pick<
  RenderAssetListItem,
  "collectionName" | "pedGender" | "kind" | "slotId" | "drawableLocalIndex"
>;

export function parsePositiveInt(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return Math.floor(parsed);
}

export function parseNonNegativeInt(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }
  return Math.floor(parsed);
}

export function formatRenderPrice(value: number): string {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatRenderCollectionLabel(value?: string | null): string {
  const normalized = (value ?? "").trim();
  return normalized || "base";
}

export function buildClothingShopHash(
  asset: ClothingShopHashTarget,
  textureIndex?: number | null,
): string {
  const pedGender = asset.pedGender.trim().toLowerCase();
  const genderToken =
    pedGender === "female"
      ? "female"
      : pedGender === "unisex"
        ? "unisex"
        : "male";
  const typeToken = asset.kind === "component" ? "c" : "p";
  const normalizedCollection = asset.collectionName.trim();
  const collectionToken =
    !normalizedCollection || normalizedCollection.toLowerCase() === "default"
      ? "base"
      : normalizedCollection;
  const normalizedTextureIndex =
    typeof textureIndex === "number" && Number.isFinite(textureIndex)
      ? Math.max(0, Math.floor(textureIndex))
      : 0;

  return `${genderToken}_${typeToken}_${asset.slotId}_${collectionToken}_${asset.drawableLocalIndex}_${normalizedTextureIndex}`;
}

export function renderAssetPriceLabel(summary?: PriceSummaryLike): string {
  if (!summary) {
    return "Chưa có giá";
  }
  if (summary.defaultPrice !== undefined) {
    return formatRenderPrice(summary.defaultPrice);
  }
  if (summary.minPrice !== undefined && summary.maxPrice !== undefined) {
    if (summary.uniform || summary.minPrice === summary.maxPrice) {
      return formatRenderPrice(summary.minPrice);
    }
    return `${formatRenderPrice(summary.minPrice)} - ${formatRenderPrice(summary.maxPrice)}`;
  }
  return "Chưa có giá";
}

export function buildRenderAssetPath(asset: RenderAssetRouteTarget): string {
  const encodeSegment = (value: string, fallback: string) =>
    encodeURIComponent(value.trim() || fallback);
  return `/assets/clothes/${encodeSegment(asset.collectionName, "base")}/${encodeSegment(asset.pedGender, "unknown")}/${encodeSegment(asset.kind, "component")}/${asset.slotId}/${asset.drawableLocalIndex}/${asset.meshPartIndex}/${encodeURIComponent(asset.id)}`;
}

export function buildPaginationItems(
  currentPage: number,
  totalPages: number,
): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: Array<number | "ellipsis"> = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    items.push("ellipsis");
  }
  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }
  if (end < totalPages - 1) {
    items.push("ellipsis");
  }
  items.push(totalPages);

  return items;
}

export function resolveRenderAssetDefaultTextureIndex(
  asset: DefaultTextureAsset,
): number {
  const expected = asset.defaultTextureName.trim().toLowerCase();
  const named = asset.textureVariants.find(
    (variant) => variant.textureName.trim().toLowerCase() === expected,
  );
  if (named) {
    return named.textureIndex;
  }
  if (asset.textureVariants.some((variant) => variant.textureIndex === 0)) {
    return 0;
  }
  return asset.textureVariants[0]?.textureIndex ?? 0;
}

export function compactAssetPath(value: string): {
  name: string;
  context: string;
} {
  const segments = value.replace(/\\/g, "/").split("/").filter(Boolean);

  if (segments.length === 0) {
    return { name: value, context: "" };
  }

  const name = segments[segments.length - 1];
  const contextSegments =
    segments.length > 3 ? segments.slice(-3, -1) : segments.slice(0, -1);

  return {
    name,
    context: contextSegments.join(" / "),
  };
}
